import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  split,
  Observable,
  ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import {
  getAccessToken,
  clearTokens,
  setToken,
  setRefreshToken,
  getRefreshToken,
} from '../utils/tokenManager';
import { isImpersonating, getImpersonationToken } from '../utils/impersonationSession';
import { logger } from '../utils/logger';
import { emitSessionEvicted } from '../utils/sessionEvictionEvent';
import { currentLocationAsReturnTo, goToSignIn } from '../utils/signInRedirect';

/**
 * Check if a response contains a union type Error with errorCode 401
 * The API returns 401 errors as { __typename: 'Error', errorCode: 401, message: 'Unauthorized' }
 * within the data response, NOT as GraphQL errors or HTTP status codes.
 */
const hasUnauthorizedUnionTypeError = (
  data: Record<string, unknown> | null | undefined
): boolean => {
  if (!data || typeof data !== 'object') return false;

  // Check each field in the response data
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const typedValue = value as Record<string, unknown>;
      // Check for union type Error with 401 errorCode
      if (
        typedValue.__typename === 'Error' &&
        (typedValue.errorCode === 401 ||
          typedValue.message === 'Unauthorized' ||
          typedValue.message === 'Unauthorized - Admin access required')
      ) {
        return true;
      }
    }
  }
  return false;
};

// Flag to prevent multiple redirects when multiple queries return 401 simultaneously
let isRedirectingToSignIn = false;

/**
 * Export function to check if a redirect to sign-in is in progress
 * This allows other components (like AuthChecker) to avoid duplicate redirects
 */
export const isRedirecting = (): boolean => isRedirectingToSignIn;

// Reference to Apollo client (will be set after client creation)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let apolloClientRef: any = null;

/**
 * Centralized function to handle redirect to sign-in page
 * Prevents multiple simultaneous redirects and clears all state
 * Similar to SP app's handleUnauthorized function
 */
const redirectToSignIn = (errorCode?: string) => {
  // If the session was evicted (another device signed in), show the re-auth modal
  // instead of clearing tokens and redirecting — this preserves the user's page state
  if (errorCode === 'SESSION_EVICTED') {
    emitSessionEvicted();
    return;
  }

  // No `MISSING_REFRESH_TOKEN` guard here, deliberately, though the SP app has
  // one. SP's exists because its `tokenManager` listens for the storage event
  // and logs every open tab out when one clears its tokens; this app has no
  // such listener, so there is no cascade to prevent. Returning early on that
  // code would instead strand the client: by the time this runs the server has
  // already refused their access token, and skipping the redirect would leave
  // them on a page whose every query fails, with no way out.

  // If already redirecting, skip
  if (isRedirectingToSignIn) {
    logger.info('[Apollo] Already redirecting to sign-in, skipping duplicate redirect');
    return;
  }

  // Check if we're already on the signin page to avoid unnecessary redirects
  if (window.location.pathname === '/signin') {
    logger.info('[Apollo] Already on sign-in page, skipping redirect');
    return;
  }

  // Set flag to prevent multiple redirects - MUST be set BEFORE clearing tokens
  // This ensures AuthChecker sees the flag when it re-runs due to token change
  isRedirectingToSignIn = true;
  const returnTo = currentLocationAsReturnTo();
  // The reason goes in the log: "why was I signed out" is the question this
  // whole path exists to answer, and it used to leave no trace of which of the
  // refusals it was.
  logger.info('[Apollo] Initiating redirect to sign-in', { currentPath: returnTo, errorCode });

  clearTokens();

  // `stop()` only — no `clearStore()`. Clearing the store makes every watched
  // query repaint empty, which is the blank flash the client sees in the
  // instant before the page goes away; stopping them keeps whatever is on
  // screen on screen until the navigation replaces it.
  if (apolloClientRef) {
    apolloClientRef.stop();
  }

  // Straight out, with no timer in between. The redirect used to wait 100ms for
  // a toast that the code's own comment admitted might never be seen; the
  // message is on the sign-in page now, so there is nothing left to wait for.
  goToSignIn(returnTo, { expired: true });
};

/**
 * Afterware link to detect 401 errors in union type responses
 * This handles the case where the API returns 401 as part of the successful GraphQL response
 * rather than as a GraphQL error or HTTP status code.
 */
const unionTypeErrorLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    // Check if the response data contains a 401 union type error
    if (
      response.data &&
      hasUnauthorizedUnionTypeError(response.data as Record<string, unknown>) &&
      !isRedirectingToSignIn
    ) {
      logger.warn(
        `[UNION_TYPE_401] Detected unauthorized error in response for operation: ${operation.operationName}`
      );

      // Try to refresh the token before redirecting. If the refresh reveals
      // the session was evicted, redirectToSignIn will show the modal instead.
      // Through the shared guard: this path used to call the refresh directly,
      // so a second operation failing a few milliseconds later started a
      // second one against an already-rotated token.
      refreshSessionOnce()
        .then(result => {
          if (result.success && result.newToken) {
            // Token refreshed successfully — downstream code may re-fetch
            return;
          }
          redirectToSignIn(result.errorCode);
        })
        .catch(() => {
          redirectToSignIn();
        });
    }

    return response;
  });
});

interface RefreshResult {
  success: boolean;
  newToken?: string;
  errorCode?: string;
}

// Track pending refresh token promise to prevent concurrent refreshes
let refreshTokenPromise: Promise<RefreshResult> | null = null;

/**
 * How long the portal will wait on a refresh before deciding the session is
 * gone. The mutation is a single round trip; ten seconds is already far past
 * the point where it is going to answer, and whatever it does after that the
 * client has been staring at a half-dead page the whole time.
 */
const REFRESH_TIMEOUT_MS = 10000;

/**
 * One refresh per wave of unauthorized responses.
 *
 * Every path that sees a 401 goes through here. They used to each start their
 * own: the two in `errorLink` shared `refreshTokenPromise`, but
 * `unionTypeErrorLink` called `attemptTokenRefresh()` directly, so two
 * operations failing milliseconds apart began two refreshes, and the portal
 * sat through both in series before deciding anything.
 *
 * The second one is not rejected for using a rotated token, which is worth
 * saying because it is the obvious guess: the `refreshAccessToken` mutation
 * keeps the previous refresh token valid for a grace window precisely so a
 * second tab does not log the first one out. What the duplicate costs is time
 * — a whole extra round trip against an API that may be slow, which is most of
 * the window the client spends watching a page whose every panel has failed.
 */
const refreshSessionOnce = (): Promise<RefreshResult> => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = attemptTokenRefresh().finally(() => {
      // Cleared here rather than by each caller: a caller that cleared it in
      // its own `then` would free the slot for the next 401 in the same wave.
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

const httpLink = createHttpLink({
  uri: import.meta.env['VITE_GRAPHQL_ENDPOINT'] || 'http://localhost:4000/graphql',
});

// Create WebSocket link for subscriptions with auto-reconnect
const wsLink = new GraphQLWsLink(
  createClient({
    url: (import.meta.env['VITE_GRAPHQL_ENDPOINT'] || 'http://localhost:4000/graphql').replace(
      'http',
      'ws'
    ),
    connectionParams: () => {
      const token = isImpersonating() ? getImpersonationToken() : getAccessToken();
      return {
        authorization: token ? `Bearer ${token}` : '',
      };
    },
    // Enable automatic reconnection
    retryAttempts: Infinity,
    shouldRetry: () => true,
    retryWait: async retries => {
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const delay = Math.min(1000 * Math.pow(2, retries), 30000);
      logger.info(`[WebSocket] Reconnecting in ${delay}ms (attempt ${retries + 1})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    },
    on: {
      connected: () => logger.info('[WebSocket] Connected'),
      closed: event => {
        const code = (event as CloseEvent)?.code;
        const reason = (event as CloseEvent)?.reason || 'Unknown';
        logger.info(`[WebSocket] Closed (code: ${code}, reason: ${reason})`);
      },
      error: error => logger.error({ message: '[WebSocket] Error', context: { error } }),
    },
  })
);

const authLink = setContext(async (_, { headers }) => {
  const token = isImpersonating() ? getImpersonationToken() : getAccessToken();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

/**
 * Attempts to refresh the access token inline (for errorLink use)
 */
const attemptTokenRefresh = async (): Promise<RefreshResult> => {
  if (isImpersonating()) {
    return { success: false };
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // Named, so `redirectToSignIn` can tell this apart from a refresh the
    // server turned down and leave the other tabs alone.
    return { success: false, errorCode: 'MISSING_REFRESH_TOKEN' };
  }

  // The refresh used to be a bare fetch with no bound, so the decision to
  // leave inherited whatever the API was taking at that moment, and the client
  // waited on a dead page for as long as it took.
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), REFRESH_TIMEOUT_MS);

  try {
    // Use fetch directly to avoid Apollo client circular dependency
    const response = await fetch(
      import.meta.env['VITE_GRAPHQL_ENDPOINT'] || 'http://localhost:4000/graphql',
      {
        method: 'POST',
        signal: abortController.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `
            mutation RefreshAccessToken($data: RefreshAccessTokenInput!) {
              refreshAccessToken(data: $data) {
                ... on RefreshAccessToken {
                  accessToken
                  refreshToken
                }
                ... on Error {
                  message
                  errorCode
                }
              }
            }
          `,
          variables: { data: { refreshToken } },
        }),
      }
    );

    const result = await response.json();

    if (result.data?.refreshAccessToken?.accessToken) {
      const { accessToken, refreshToken: newRefreshToken } = result.data.refreshAccessToken;
      setToken(accessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
      return { success: true, newToken: accessToken };
    }

    // Check for session eviction in the error response message
    const errorMessage = result.data?.refreshAccessToken?.message || '';
    const isSessionEvicted = errorMessage.toLowerCase().includes('session evicted');
    if (isSessionEvicted) {
      return { success: false, errorCode: 'SESSION_EVICTED' };
    }
    return { success: false };
  } catch (error) {
    const timedOut = error instanceof DOMException && error.name === 'AbortError';
    logger.error({
      message: timedOut
        ? `Token refresh timed out after ${REFRESH_TIMEOUT_MS}ms`
        : 'Token refresh failed in errorLink',
      context: { error: error instanceof Error ? error.message : 'Unknown error' },
    });
    return { success: false };
  } finally {
    clearTimeout(timeoutId);
  }
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // Get context to check for skipRefresh flag
  const context = operation.getContext();
  const skipRefresh = context.skipRefresh === true;

  // Skip refresh logic for the refresh token mutation itself to prevent infinite loops
  if (operation.operationName === 'RefreshAccessToken' || skipRefresh) {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message }) => {
        logger.error({
          message: `GraphQL error (skip refresh): ${message}`,
          context: { operation: operation.operationName },
        });
      });
    }
    return;
  }

  if (graphQLErrors) {
    // Check for unauthorized errors
    const unauthorizedError = graphQLErrors.find(
      ({ message, extensions }) =>
        message === 'Unauthorized' || extensions?.['code'] === 'UNAUTHENTICATED'
    );

    if (unauthorizedError) {
      // Attempt token refresh before giving up
      logger.info(`[TOKEN REFRESH] Joining refresh for: ${operation.operationName}`);

      return new Observable(observer => {
        refreshSessionOnce()
          .then(result => {
            if (result.success && result.newToken) {
              logger.info(`[TOKEN REFRESH] Success, retrying: ${operation.operationName}`);

              // Update the operation context with the new token
              operation.setContext({
                ...context,
                headers: {
                  ...context.headers,
                  authorization: `Bearer ${result.newToken}`,
                },
              });

              // Retry the operation
              forward(operation).subscribe({
                next: value => observer.next(value),
                error: err => observer.error(err),
                complete: () => observer.complete(),
              });
            } else {
              logger.warn('Token refresh failed, redirecting to sign in');
              redirectToSignIn(result.errorCode);
              observer.complete();
            }
          })
          .catch(() => {
            logger.warn('Token refresh error, redirecting to sign in');
            redirectToSignIn();
            observer.complete();
          });
      });
    }

    // Log non-auth errors
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      logger.error({
        message: `GraphQL error: ${message}`,
        context: {
          operation: operation.operationName,
          locations: JSON.stringify(locations),
          path: JSON.stringify(path),
          extensions: JSON.stringify(extensions),
        },
      });
    });
  }

  if (networkError) {
    const networkContext = {
      operation: operation.operationName,
      variables: operation.variables,
      statusCode: 'statusCode' in networkError ? networkError.statusCode : null,
    };

    logger.error({
      message: `Network error: ${networkError.message}`,
      error: networkError,
      context: networkContext,
    });

    // Handle 401 network errors with token refresh
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      return new Observable(observer => {
        refreshSessionOnce()
          .then(result => {
            if (result.success && result.newToken) {
              operation.setContext({
                ...context,
                headers: {
                  ...context.headers,
                  authorization: `Bearer ${result.newToken}`,
                },
              });

              forward(operation).subscribe({
                next: value => observer.next(value),
                error: err => observer.error(err),
                complete: () => observer.complete(),
              });
            } else {
              logger.warn('Token refresh failed on 401, redirecting to sign in');
              redirectToSignIn(result.errorCode);
              observer.complete();
            }
          })
          .catch(() => {
            redirectToSignIn();
            observer.complete();
          });
      });
    }

    // Handle other network errors (server errors, timeouts, etc.)
    const statusCode = 'statusCode' in networkError ? networkError.statusCode : null;
    const isServerError = statusCode && statusCode >= 500;
    const isForbidden = statusCode === 403;
    const isTimeout = networkError.message?.toLowerCase().includes('timeout');
    const isFetchFailed = networkError.message?.toLowerCase().includes('failed to fetch');

    // Only logout if we have a token (user was logged in) and it's a definitive auth failure
    const hasToken = !!getAccessToken();

    if (hasToken && isForbidden) {
      logger.warn('Forbidden error, clearing tokens and redirecting');
      redirectToSignIn();
      return;
    }

    // For server errors and network issues, don't immediately logout
    // Let the user retry when connectivity is restored
    if (isServerError || isTimeout || isFetchFailed) {
      logger.warn('Network/server issue detected, not logging out', networkContext);
      // Don't logout - let the error propagate so UI can show error message
    }
  }

  return undefined;
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: error => {
      return !!error && error.statusCode >= 500;
    },
  },
});

const cache = new InMemoryCache({
  typePolicies: {
    User: {
      fields: {
        posts: {
          merge(existing = [], incoming) {
            return [...existing, ...incoming];
          },
        },
      },
    },
    Job: {
      fields: {
        status: {
          merge(existing, incoming) {
            if (
              (existing === 'COMPLETED' || existing === 'CANCELED') &&
              incoming &&
              incoming !== existing
            ) {
              return existing;
            }
            return incoming;
          },
        },
      },
    },
    Query: {
      fields: {
        users: {
          keyArgs: ['filters'],
          merge(existing = { edges: [] }, incoming) {
            return {
              ...incoming,
              edges: [...existing.edges, ...incoming.edges],
            };
          },
        },
        getMessages: {
          // Don't merge with existing cached data - always use fresh response
          merge: false,
        },
      },
    },
  },
});

// Split link: use WebSocket for subscriptions, HTTP for queries/mutations
// The link chain order is important:
// 1. unionTypeErrorLink - detects 401 in union type responses (afterware, runs after response)
// 2. errorLink - handles GraphQL errors and network 401s, attempts token refresh
// 3. authLink - adds authorization header to requests
// 4. retryLink - retries failed requests
// 5. httpLink - sends the actual HTTP request
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  from([unionTypeErrorLink, errorLink, authLink, retryLink, httpLink])
);

export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Set the reference for redirectToSignIn function
apolloClientRef = apolloClient;

export const resetApolloStore = () => {
  apolloClient.clearStore();
};
